import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, MessageCircle, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { chatService } from '../../services/api';

const LiveChat = ({ roomId, roomName, isOpen, onClose }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async () => {
    if (!roomId) return;
    try {
      const response = await chatService.getMessages(roomId, 100);
      setMessages(response.data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [roomId]);

  useEffect(() => {
    if (isOpen && roomId) {
      fetchMessages();
      // Poll for new messages every 3 seconds
      pollIntervalRef.current = setInterval(fetchMessages, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, roomId, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !isAuthenticated || !token) return;

    setLoading(true);
    setError('');

    try {
      await chatService.sendMessage(roomId, newMessage.trim(), token);
      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      setError('Failed to send message');
      console.error('Send message error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-4 bottom-4 w-96 h-[500px] bg-gray-900 rounded-xl border border-gray-800 shadow-2xl flex flex-col z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle size={20} className="text-white" />
          <span className="text-white font-semibold">{roomName || 'Live Chat'}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-white/70 text-sm">
            <Users size={16} />
            <span>{messages.length > 0 ? new Set(messages.map(m => m.user_id)).size : 0}</span>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle size={40} className="mx-auto mb-2 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Be the first to say hello!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-2 ${msg.user_id === user?.id ? 'flex-row-reverse' : ''}`}
            >
              {/* Avatar */}
              <div className="flex-shrink-0">
                {msg.user_avatar ? (
                  <img 
                    src={msg.user_avatar} 
                    alt={msg.user_name} 
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {msg.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Message */}
              <div className={`max-w-[70%] ${msg.user_id === user?.id ? 'text-right' : ''}`}>
                <p className="text-gray-500 text-xs mb-1">{msg.user_name}</p>
                <div
                  className={`rounded-lg px-3 py-2 ${
                    msg.user_id === user?.id
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-800 text-gray-200'
                  }`}
                >
                  {msg.content}
                </div>
                <p className="text-gray-600 text-xs mt-1">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isAuthenticated ? (
        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-800">
          {error && (
            <p className="text-red-400 text-sm mb-2">{error}</p>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              maxLength={500}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !newMessage.trim()}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="chat-send-btn"
            >
              <Send size={20} />
            </button>
          </div>
        </form>
      ) : (
        <div className="p-4 border-t border-gray-800 text-center">
          <p className="text-gray-400 text-sm">
            Please <span className="text-red-500 cursor-pointer">sign in</span> to chat
          </p>
        </div>
      )}
    </div>
  );
};

// Chat Toggle Button
export const ChatToggleButton = ({ onClick, unreadCount = 0 }) => (
  <button
    onClick={onClick}
    className="fixed right-4 bottom-4 bg-red-600 hover:bg-red-700 text-white p-4 rounded-full shadow-lg transition-colors z-40"
    data-testid="chat-toggle-btn"
  >
    <MessageCircle size={24} />
    {unreadCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
        {unreadCount > 9 ? '9+' : unreadCount}
      </span>
    )}
  </button>
);

export default LiveChat;
